import { useState, useMemo, useEffect, useCallback, useId, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ChevronRight, ChevronDown } from 'lucide-react';
import { pluriel } from './formatConfig';
import { useTheme } from '../../hooks/fetch_api_hook/useTheme';
import { useThemesPrecedents } from '../../hooks/fetch_api_hook/useThemesPrecedents';
import { useThemeEnCache } from '../../hooks/fetch_api_hook/useThemeEnCache';
import { useComptesQuestionsParAttendu } from '../../hooks/fetch_api_hook/useComptesQuestionsParAttendu';
import { useLiensPrerequis } from '../../hooks/fetch_api_hook/useLiensPrerequis';
import { useAttendusAvecArbre, TOUS_LES_ATTENDUS } from '../../hooks/fetch_api_hook/useAttendusAvecArbre';
import { getCategories } from '../../generators';
import { exerciseurOuvert } from '../../generators/ouverture';
import { partitionnerParRubrique } from '../../utils/rubriqueAttendus';
import { filsDeLArbre, grapheDesArbres, lignesArbre, racinesDeRecherche } from '../../utils/arbrePrerequis';
import SeparateurRubrique from '../general/SeparateurRubrique';
import normalize from '../../utils/normalize';
import { LigneAttendu, ColonneFil } from './LigneAttendu';
import { chiffreNiveau } from '../CollerNotes/PastilleNiveau';

const AUCUNE = 'aucune';
// Les niveaux du petit menu « Niveau » (chiffres de themes/?niveau=).
const NIVEAUX = ['6', '5', '4', '3'];

// Niveau d'un attendu (« 4eme » → « 4 »), sinon celui du référentiel chargé.
const niveauDe = (att, defaut) => String(att?.niveau || defaut || '').charAt(0);

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-500';

// Thème et descripteur : le chevron devant, vers le bas quand c'est ouvert.
const Chevron = ({ ouvert }) => (ouvert
    ? <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />
    : <ChevronRight size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />);

/**
 * Popup « Que voulez-vous travailler ? » (plan popup-choix-attendu, SFD F1
 * amendée le 2026-10-04) : elle remplace le panneau latéral.
 * - Saisie : les attendus du niveau de la classe qui ne sont prérequis d'aucun
 *   autre résultat (à défaut, ceux du premier niveau précédent qui répond),
 *   chacun avec son arbre de prérequis bloquants, un seul déplié à la fois.
 * - Hors séance (13/10/2026) : la navigation par thème, dépliée sur l'attendu
 *   en cours ; s'il a des prérequis, une carte les montre en tête. Sinon, la
 *   liste défile à l'ouverture pour centrer sa ligne.
 * - Pendant une séance (diagnostic, groupes) : l'arbre de l'attendu de la
 *   séance seul ; « Parcourir tous les attendus » ramène la navigation.
 * - AUTRE NIVEAU (2026-09-29, demande de deux profs : une 4e travaille un
 *   objectif de 6e) : un petit menu « Niveau » règle la navigation par thème,
 *   par défaut sur le niveau de l'attendu en cours, sinon la classe. L'arbre
 *   d'un autre niveau se charge à la demande puis reste en mémoire ; ses
 *   questions écrites à la main sont comptées par le serveur.
 * - PROVISOIRE (11/10/2026) : tant que les arbres se construisent, seuls les
 *   attendus du réglage serveur `ATTENDUS_AVEC_ARBRE` montrent leurs prérequis
 *   (leur arbre entier). Recherche et navigation proposent TOUS les attendus,
 *   les autres sans prérequis (13/10/2026 : le réglage les masquait aussi, ce
 *   n'était pas voulu) ; leurs exerciseurs, c'est `generators/ouverture.js`.
 * - DESSIN (13/10/2026, maquette validée) : une police, trois tailles ; le fil
 *   cyan (├ └ │) veut dire « s'appuie sur », le retrait seul « rangé dans » ;
 *   le bleu TanoEd marque l'attendu en cours. Plus de menu « Domaine » (il
 *   doublait le premier niveau de la navigation), plus de compteurs de thème
 *   ni de descripteur. « Objectifs » et « Automatismes » gardent leur
 *   séparateur pliable habituel (SeparateurRubrique, mêmes couleurs).
 * `attenduCourant` : `{ id, description, niveau, rubrique }` (celui de la
 * séance s'il y en a une) ; `seance` : `{ titre }` pendant un diagnostic ou
 * des groupes.
 */
const PanelAttendus = ({ open, onClose, niveauChar, questions, selectedAttenduId, attenduCourant = null, onSelectAttendu, modeRevue = false, seance = null, refreshKey }) => {
    const titreId = useId();
    const boite = useRef(null);
    const champ = useRef(null);
    const corps = useRef(null);
    const [search, setSearch] = useState('');
    const [parcourir, setParcourir] = useState(false);
    const [racineOuverte, setRacineOuverte] = useState(null);
    // Navigation par thème : un thème et un descripteur ouverts à la fois.
    const [expandedTheme, setExpandedTheme] = useState(null);
    const [expandedDesc, setExpandedDesc] = useState(null);
    // Sections de rubrique d'un descripteur partitionné : choix explicite, sinon
    // fermé, sauf la section de l'attendu sélectionné.
    const [choixSections, setChoixSections] = useState({});

    const { data: themes } = useTheme(niveauChar);
    // Niveau de la navigation par thème : le choix du prof, sinon celui de
    // l'attendu en cours, sinon la classe (remis à zéro à chaque ouverture).
    const classe = chiffreNiveau(niveauChar);
    // Le choix ne vaut que pour la classe et l'attendu en cours où il est fait.
    const cleChoix = `${classe}|${attenduCourant?.id ?? ''}`;
    const [choixNiveau, setChoixNiveau] = useState(null);
    const niveauNav = (choixNiveau?.cle === cleChoix && choixNiveau.niveau) || chiffreNiveau(attenduCourant?.niveau) || classe;
    const autreNiveau = Boolean(classe && niveauNav && niveauNav !== classe);
    const { data: themesAutre, isLoading: chargementAutre } = useThemeEnCache(niveauNav, open && autreNiveau);
    const themesNav = autreNiveau ? themesAutre : themes;
    const { data: comptesAutre } = useComptesQuestionsParAttendu(autreNiveau ? `${niveauNav}eme` : '', autreNiveau, refreshKey);
    // Les niveaux inférieurs portent les prérequis : chargés dès l'ouverture.
    const { niveaux: themesPrecedents, chargement: chargementPrecedents } = useThemesPrecedents(niveauChar, open);
    const grapheComplet = useLiensPrerequis();
    // Les prérequis ne se lisent que dans les arbres des attendus du réglage
    // serveur : tout autre attendu est proposé seul (liste pas encore chargée :
    // aucun arbre).
    const avecArbre = useAttendusAvecArbre();
    const graphe = useMemo(() => (avecArbre === TOUS_LES_ATTENDUS
        ? grapheComplet
        : grapheDesArbres(grapheComplet, avecArbre instanceof Set ? [...avecArbre] : [])), [grapheComplet, avecArbre]);

    // Chaque ouverture repart de l'arbre de l'attendu en cours, saisie vide, la
    // section de l'attendu en cours ouverte, sa ligne à centrer.
    const centree = useRef(false);
    useEffect(() => {
        if (!open) return;
        setSearch('');
        setParcourir(false);
        setRacineOuverte(null);
        setChoixNiveau(null);
        setChoixSections({});
        centree.current = false;
    }, [open]);

    // Modale : Échap ferme, Tab reste dans la popup, le focus revient ensuite.
    const fermer = useRef(onClose);
    fermer.current = onClose;
    useEffect(() => {
        if (!open) return undefined;
        const origine = document.activeElement;
        champ.current?.focus();
        const onKey = (e) => {
            // Une confirmation ouverte par-dessus garde Échap pour elle.
            const dedans = boite.current?.contains(document.activeElement);
            if (e.key === 'Escape') { if (dedans) fermer.current(); return; }
            if (e.key !== 'Tab' || !boite.current) return;
            const cibles = [...boite.current.querySelectorAll('button, input, select')];
            if (cibles.length === 0) return;
            const premier = cibles[0];
            const dernier = cibles[cibles.length - 1];
            if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
            else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
            else if (!dedans) { e.preventDefault(); premier.focus(); }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            if (origine && typeof origine.focus === 'function' && document.contains(origine)) origine.focus();
        };
    }, [open]);

    // À l'ouverture de la navigation, l'arbre est déplié sur l'attendu courant.
    useEffect(() => {
        if (!open || !selectedAttenduId || !Array.isArray(themesNav)) return;
        for (const theme of themesNav) {
            for (const desc of (theme.descripteur || [])) {
                if ((desc.attendus || []).some((a) => a.id === selectedAttenduId)) {
                    setExpandedTheme(theme.nom);
                    setExpandedDesc(`${theme.nom}::${desc.nom}`);
                    return;
                }
            }
        }
    }, [open, selectedAttenduId, themesNav]);

    // Questions du prof par attendu, créées à la main seulement (les instances
    // générées sont comptées avec leur carte exerciseur).
    const questionsByAttendu = useMemo(() => {
        const map = new Map();
        (questions || []).forEach((q) => {
            if (q.attendu && !q.generator_key) map.set(q.attendu, (map.get(q.attendu) || 0) + 1);
        });
        return map;
    }, [questions]);

    // Un attendu avec son nombre d'exercices (exerciseur ouvert ou non). Les
    // questions écrites à la main : la banque déjà chargée pour la classe, le
    // serveur pour l'autre niveau de la navigation (vide avant réponse).
    const enrichir = useCallback((att, niveau) => {
        const ouvert = exerciseurOuvert(niveau, { rubrique: att.rubrique, attenduId: att.id });
        const catCount = ouvert ? getCategories(att.id).length : 0;
        const profCount = autreNiveau && chiffreNiveau(niveau) === niveauNav && comptesAutre?.[String(att.id)] !== undefined
            ? (Number(comptesAutre[String(att.id)]) || 0)
            : (questionsByAttendu.get(att.id) || 0);
        return {
            id: att.id, description: att.description, rubrique: att.rubrique, niveau: att.niveau,
            totalExos: catCount + profCount,
        };
    }, [questionsByAttendu, autreNiveau, niveauNav, comptesAutre]);

    // Tous les attendus chargés, par id : de quoi nommer chaque ligne d'arbre.
    const parId = useMemo(() => {
        const index = new Map();
        const ajouter = (ths, niveau) => (ths || []).forEach((theme) => (theme.descripteur || []).forEach((desc) => (desc.attendus || []).forEach((att) => {
            if (!index.has(att.id)) index.set(att.id, { att, niveau: niveauDe(att, niveau), themeNom: theme.nom, descNom: desc.nom });
        })));
        ajouter(Array.isArray(themes) ? themes : [], niveauChar);
        themesPrecedents.forEach(({ niveau, themes: ths }) => ajouter(ths, niveau));
        if (autreNiveau) ajouter(Array.isArray(themesAutre) ? themesAutre : [], niveauNav);
        return index;
    }, [themes, themesPrecedents, niveauChar, autreNiveau, themesAutre, niveauNav]);

    const terme = normalize(search.trim());

    // À l'ouverture, un attendu sans carte de prérequis (13/10/2026) : la liste
    // défile pour centrer sa ligne, une fois par ouverture. Rien pendant une
    // recherche, ni quand la carte de l'arbre est en tête : elle le montre déjà.
    // Après chaque rendu : la ligne n'existe qu'une fois la navigation chargée
    // et dépliée sur lui.
    useEffect(() => {
        if (!open || centree.current || !selectedAttenduId || terme) return;
        const zone = corps.current;
        if (!zone || zone.querySelector('#popup-arbre')) return;
        const ligne = zone.querySelector('[aria-current="true"]');
        if (!ligne) return;
        centree.current = true;
        ligne.scrollIntoView?.({ block: 'center' });
    });

    // Recherche : par niveau, celui de la classe d'abord.
    const groupesRecherche = useMemo(() => {
        if (!terme) return [];
        const ids = (ths) => ths.flatMap((theme) => (theme.descripteur || []).flatMap((desc) => {
            const descRetenu = normalize(desc.nom).includes(terme);
            return (desc.attendus || []).filter((att) => descRetenu || normalize(att.description).includes(terme)).map((att) => att.id);
        }));
        return [
            { niveau: String(niveauChar), ids: ids(Array.isArray(themes) ? themes : []) },
            ...themesPrecedents.map(({ niveau, themes: ths }) => ({ niveau, ids: ids(ths) })),
        ];
    }, [terme, themes, themesPrecedents, niveauChar]);
    const trouves = useMemo(() => (terme ? racinesDeRecherche(groupesRecherche, graphe) : null), [terme, groupesRecherche, graphe]);
    // Racine dépliée : le choix du prof (`AUCUNE` : tout replié), sinon la
    // première qui a un arbre.
    let racineDepliee = null;
    if (trouves && racineOuverte !== AUCUNE) {
        racineDepliee = trouves.ids.includes(racineOuverte) ? racineOuverte
            : trouves.ids.find((id) => (graphe[id] || []).length > 0) ?? null;
    }

    // Navigation par thème : chaque descripteur avec ses objectifs et ses automatismes.
    const tree = useMemo(() => (open && Array.isArray(themesNav) ? themesNav : [])
        .map((theme) => ({
            nom: theme.nom,
            descs: (theme.descripteur || []).map((desc) => {
                const attendus = (desc.attendus || []).map((att) => enrichir(att, niveauNav));
                return { nom: desc.nom, attendus, ...partitionnerParRubrique(attendus) };
            }).filter((d) => d.attendus.length > 0),
        }))
        .filter((t) => t.descs.length > 0), [open, themesNav, enrichir, niveauNav]);

    const choisir = useCallback((att) => {
        const info = parId.get(att.id);
        onSelectAttendu({
            id: att.id, description: att.description, rubrique: att.rubrique,
            niveau: att.niveau || info?.att.niveau, themeName: info?.themeNom, descName: info?.descNom,
        });
    }, [parId, onSelectAttendu]);

    // Lignes d'arbre d'une racine, nommées ; un prérequis non chargé est sauté.
    // Provisoire (11/10/2026, comme le diagnostic) : rien sous le niveau
    // précédent de la racine (une 4e descend en 5e, pas en 6e).
    const lignesDe = (racine, racineNiveau) => lignesArbre(racine.id, graphe, (id) => {
        const niveau = Number(parId.get(id)?.niveau);
        return !niveau || !Number(racineNiveau) || niveau <= Number(racineNiveau) + 1;
    }).flatMap(({ id, profondeur }) => {
        if (profondeur === 0) return [{ att: enrichir(racine, racineNiveau), niveau: racineNiveau, profondeur }];
        const info = parId.get(id);
        return info ? [{ att: enrichir(info.att, info.niveau), niveau: info.niveau, profondeur }] : [];
    });

    // Les lignes d'un arbre sur leur fil (13/10/2026). La racine porte le tronc :
    // son point si elle a des prérequis, le départ du fil s'ils sont affichés.
    // `legende` : « s'appuie sur ces N prérequis : » sous la racine (la carte).
    const renderArbre = (lignes, { aDesPrerequis, etatRacine = null, legende = false }) => {
        const fils = filsDeLArbre(lignes);
        const nb = lignes.length - 1;
        return lignes.map(({ att, niveau, profondeur }, i) => (
            <LigneAttendu
                key={att.id} att={att} niveau={niveau} profondeur={profondeur} terme={terme}
                fils={i > 0 ? fils[i] : [nb > 0 ? 'tronc' : (aDesPrerequis ? 'point' : 'vide')]}
                etat={i === 0 ? etatRacine : null} actif={selectedAttenduId === att.id}
                onChoisir={choisir} modeRevue={modeRevue}
            >
                {i === 0 && legende && nb > 0 ? (
                    <div className="flex items-stretch">
                        <ColonneFil morceau="passe" />
                        <p className="py-1 pl-2 text-[13px] font-semibold text-slate-600">
                            {nb > 1 ? `s’appuie sur ces ${nb} prérequis :` : 's’appuie sur ce prérequis :'}
                        </p>
                    </div>
                ) : null}
            </LigneAttendu>
        ));
    };

    // La carte de l'arbre d'un attendu : son titre, l'arbre, une aide.
    const carteArbre = (titre, lignes, aide = null) => (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm px-4 pt-3 pb-3.5">
            <p id="popup-arbre" className="text-[13px] font-bold text-blue-900">{titre}</p>
            <ul aria-labelledby="popup-arbre" className="mt-0.5">
                {renderArbre(lignes, { aDesPrerequis: lignes.length > 1, etatRacine: 'en-cours', legende: true })}
            </ul>
            {aide ? <p className="mt-2 ml-9 text-[13px] text-slate-500">{aide}</p> : null}
        </div>
    );

    const toggleTheme = (name) => setExpandedTheme((prev) => (prev === name ? null : name));
    const toggleDesc = (key) => setExpandedDesc((prev) => (prev === key ? null : key));
    const toggleSection = (cle, ouvertActuel) => setChoixSections((prev) => ({ ...prev, [cle]: !ouvertActuel }));

    if (!open) return null;

    // Annonce de la recherche : zone `status` toujours montée, seul son texte change.
    let annonce = '';
    if (terme) {
        annonce = trouves ? `${trouves.niveau}e · ${pluriel(trouves.ids.length, 'attendu')}`
            : chargementPrecedents ? 'Recherche dans les niveaux précédents…' : 'Aucun résultat';
    }

    // ── Contenu ──
    let contenu;
    if (terme) {
        contenu = trouves ? (
            <ul aria-label="Attendus trouvés">
                {trouves.ids.map((id) => {
                    const info = parId.get(id);
                    if (!info) return null;
                    const lignes = lignesDe(info.att, info.niveau);
                    const deplie = racineDepliee === id;
                    return (
                        <li key={id} className="border-b border-slate-100 last:border-b-0 py-1">
                            <ul>{renderArbre(deplie ? lignes : lignes.slice(0, 1), { aDesPrerequis: lignes.length > 1 })}</ul>
                            {lignes.length > 1 ? (
                                <button
                                    type="button"
                                    aria-expanded={deplie}
                                    onClick={() => setRacineOuverte(deplie ? AUCUNE : id)}
                                    className={`ml-7 min-h-[44px] px-2 rounded text-[13px] font-semibold text-blue-900 hover:underline ${FOCUS}`}
                                >
                                    {deplie ? 'Masquer les prérequis' : (lignes.length > 2 ? `Voir ses ${lignes.length - 1} prérequis` : 'Voir son prérequis')}
                                </button>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        ) : null;
    } else if (attenduCourant && seance && !parcourir) {
        // Pendant une séance (diagnostic, groupes) : l'attendu de la séance et ses
        // prérequis seuls ; en sortir démarre une nouvelle séance.
        contenu = (
            <div className="flex flex-col gap-5">
                {carteArbre('Attendu de la séance et ses prérequis', lignesDe(attenduCourant, niveauDe(attenduCourant, niveauChar)))}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                    <button
                        type="button"
                        onClick={() => { setParcourir(true); champ.current?.focus(); }}
                        className={`h-11 px-4 rounded-xl border border-slate-300 bg-white text-[15px] font-bold text-blue-900 hover:bg-slate-50 ${FOCUS}`}
                    >
                        Parcourir tous les attendus de {niveauNav}e
                    </button>
                    <span className="flex-1 min-w-[240px] text-[13px] text-slate-500">Chercher un autre attendu démarre une nouvelle session (on vous demande d’abord).</span>
                </div>
            </div>
        );
    } else {
        // Hors séance (13/10/2026) : la navigation par thème toujours visible, dépliée
        // sur l'attendu en cours ; s'il a des prérequis (arbre validé), ils viennent
        // en tête, dans une carte.
        const lignesEnCours = attenduCourant && !seance ? lignesDe(attenduCourant, niveauDe(attenduCourant, niveauChar)) : [];
        // Une ligne de la navigation : ni fil, ni niveau (le titre le donne).
        const ligne = (att) => (
            <LigneAttendu key={att.id} att={att} niveau={niveauDe(att, niveauNav)} avecNiveau={false} actif={selectedAttenduId === att.id} onChoisir={choisir} modeRevue={modeRevue} />
        );
        const navigation = autreNiveau && chargementAutre ? (
            <p className="text-sm text-center py-8 text-slate-600">Chargement des attendus de {niveauNav}e…</p>
        ) : tree.length === 0 ? (
            <p className="text-sm text-center py-8 text-slate-600">Aucun résultat</p>
        ) : tree.map((theme) => {
            const ouvert = expandedTheme === theme.nom;
            return (
                <div key={theme.nom} className="border-b border-slate-100 last:border-b-0">
                    <button type="button" aria-expanded={ouvert} onClick={() => toggleTheme(theme.nom)} className={`w-full text-left flex items-center gap-2.5 px-2 min-h-[48px] rounded-lg text-slate-500 hover:bg-slate-50 ${FOCUS}`}>
                        <Chevron ouvert={ouvert} />
                        <span className="flex-1 text-base font-bold text-slate-900">{theme.nom}</span>
                    </button>
                    {ouvert ? (
                        <div className="pl-[26px] pb-1">
                            {theme.descs.map((desc) => {
                                const descKey = `${theme.nom}::${desc.nom}`;
                                const descOuvert = expandedDesc === descKey;
                                // Objectifs et automatismes : le séparateur pliable habituel, la
                                // section de l'attendu en cours ouverte d'office.
                                const cleObjs = `${descKey}::objs`;
                                const cleAutos = `${descKey}::autos`;
                                const objectifsOuverts = choixSections[cleObjs] ?? desc.objectifs.some((a) => a.id === selectedAttenduId);
                                const autosOuverts = choixSections[cleAutos] ?? desc.automatismes.some((a) => a.id === selectedAttenduId);
                                return (
                                    <div key={descKey}>
                                        <button type="button" aria-expanded={descOuvert} onClick={() => toggleDesc(descKey)} className={`w-full text-left flex items-center gap-2.5 px-2 min-h-[48px] rounded-lg text-slate-500 hover:bg-slate-50 ${FOCUS}`}>
                                            <Chevron ouvert={descOuvert} />
                                            <span className={`flex-1 text-base leading-snug text-slate-800 ${descOuvert ? 'font-bold' : 'font-semibold'}`}>{desc.nom}</span>
                                        </button>
                                        {descOuvert ? (
                                            <div className="pl-[26px] pb-2">
                                                {desc.automatismes.length === 0 ? <ul>{desc.attendus.map(ligne)}</ul> : (
                                                    <>
                                                        {desc.objectifs.length > 0 ? (
                                                            <SeparateurRubrique rubrique="objectif" className="px-2" count={desc.objectifs.length} ouvert={objectifsOuverts} onToggle={() => toggleSection(cleObjs, objectifsOuverts)} />
                                                        ) : null}
                                                        {desc.objectifs.length > 0 && objectifsOuverts ? <ul>{desc.objectifs.map(ligne)}</ul> : null}
                                                        <SeparateurRubrique rubrique="automatisme" className="px-2" count={desc.automatismes.length} ouvert={autosOuverts} onToggle={() => toggleSection(cleAutos, autosOuverts)} />
                                                        {autosOuverts ? <ul>{desc.automatismes.map(ligne)}</ul> : null}
                                                    </>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            );
        });
        contenu = (
            <div className="flex flex-col gap-6">
                {lignesEnCours.length > 1 ? carteArbre('Attendu en cours et ses prérequis', lignesEnCours, 'Un clic sur un prérequis l’ouvre à la place.') : null}
                <div>
                    <h3 className="px-2 pb-1.5 text-base font-extrabold text-slate-900">Tous les attendus de {niveauNav}e</h3>
                    {navigation}
                </div>
            </div>
        );
    }

    return createPortal(
        <div
            className="fixed inset-0 z-[9000] flex items-start justify-center bg-slate-900/40 sm:p-10"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div ref={boite} role="dialog" aria-modal="true" aria-labelledby={titreId} className="bg-white w-full h-full sm:h-auto sm:max-h-[calc(100vh-80px)] sm:max-w-[900px] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center gap-3 px-6 pt-5 pb-2">
                    <h2 id={titreId} className="flex-1 text-xl font-extrabold text-slate-900">Que voulez-vous travailler ?</h2>
                    <button type="button" onClick={onClose} aria-label="Fermer" className={`w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 ${FOCUS}`}>
                        <X size={20} aria-hidden="true" />
                    </button>
                </div>
                <div className="flex flex-wrap gap-2.5 px-6 pt-2 pb-4">
                    <label className="flex-1 min-w-[220px] flex items-center gap-2.5 h-12 px-3.5 rounded-xl border border-slate-300 bg-slate-50 focus-within:border-blue-900 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100">
                        <Search size={18} aria-hidden="true" className="text-slate-500" />
                        <span className="sr-only">Rechercher un attendu</span>
                        <input
                            ref={champ}
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher : puissance, fraction, relatifs…"
                            className="flex-1 min-w-0 text-base text-slate-900 placeholder:text-slate-500 bg-transparent outline-none"
                        />
                    </label>
                    {classe ? (
                        <label className="flex items-center">
                            <span className="sr-only">Niveau des attendus</span>
                            <select
                                value={niveauNav}
                                onChange={(e) => { setChoixNiveau({ cle: cleChoix, niveau: e.target.value }); setParcourir(true); }}
                                className="h-12 border border-slate-300 rounded-xl px-3 text-base font-semibold text-slate-900 bg-white"
                            >
                                {NIVEAUX.map((n) => <option key={n} value={n}>{n}e{n === classe ? ' (classe)' : ''}</option>)}
                            </select>
                        </label>
                    ) : null}
                </div>
                {autreNiveau && !terme && (parcourir || !attenduCourant) && (
                    <p className="mx-6 mb-3 px-3.5 py-2.5 rounded-xl bg-amber-50 text-amber-900 text-sm font-semibold">
                        Attendus de {niveauNav}e · votre classe est en {classe}e
                    </p>
                )}
                {seance && !terme && (
                    <p className="mx-6 mb-3 px-3.5 py-2.5 rounded-xl bg-violet-50 text-violet-800 text-sm font-semibold">
                        {seance.titre}. Un prérequis cliqué s’ouvre pour ajouter ses exercices à cette séance.
                    </p>
                )}
                <div ref={corps} className="flex-1 overflow-auto border-t border-slate-200 px-6 pt-4 pb-6">
                    <p role="status" className={terme && !trouves && !chargementPrecedents ? 'text-sm text-center py-8 text-slate-600' : `text-[13px] font-semibold text-slate-600 ${annonce ? 'pb-2' : ''}`}>
                        {annonce}
                    </p>
                    {contenu}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default memo(PanelAttendus);
